import { useApp } from "@/context/AppContext";
import { Link, useNavigate } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Globe, LogOut, LayoutDashboard, BookOpen, GraduationCap, Shield, Check, UserCircle } from "lucide-react";

export function Header({ variant = "app" }) {
  const { user, lang, changeLang, t, logout, LANGUAGES, accessStatus } = useApp();
  const navigate = useNavigate();
  const approved = accessStatus === "approved";

  return (
    <header className="sticky top-0 z-50 h-20 px-4 sm:px-8 flex items-center justify-between backdrop-blur-xl bg-[#0d0d0d]/90 border-b border-[#D21F2A]/20 relative overflow-hidden">
      <Link to={user ? "/dashboard" : "/"} className="relative z-10 flex items-center gap-3 focusable" data-testid="header-logo-link">
        <img src="/logo-4flair.png" alt="4Flair" className="h-11 w-auto" />
        <span className="hidden sm:flex flex-col leading-tight">
          <span className="font-serif text-[#FFFFFF] text-lg">{t("course")}</span>
        </span>
      </Link>

      <nav className="relative z-10 flex items-center gap-1 sm:gap-2">
        {user && approved && (
          <div className="hidden md:flex items-center gap-1">
            <NavBtn to="/dashboard" icon={LayoutDashboard} label={t("nav_dashboard")} testid="nav-dashboard" />
            <NavBtn to="/chapters" icon={BookOpen} label={t("nav_chapters")} testid="nav-chapters" />
            <NavBtn to="/final" icon={GraduationCap} label={t("nav_final")} testid="nav-final" />
          </div>
        )}
        {user?.role === "admin" && (
          <NavBtn to="/admin" icon={Shield} label={t("nav_admin")} testid="nav-admin" />
        )}
        {user && (
          <NavBtn to="/profile" icon={UserCircle} label={t("nav_profile")} testid="nav-profile" />
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="language-switcher" className="focusable flex items-center gap-2 px-3 py-2 rounded-lg text-[#FFFFFF] hover:bg-white/5 transition-colors">
              <Globe className="w-4 h-4 text-[#D21F2A]" />
              <span className="text-sm hidden sm:inline">{LANGUAGES.find((l) => l.code === lang)?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#141414] text-[#FFFFFF] border border-[#D21F2A]/30 shadow-2xl min-w-[190px]">
            {LANGUAGES.map((l) => (
              <DropdownMenuItem key={l.code} data-testid={`lang-option-${l.code}`}
                onClick={() => changeLang(l.code)}
                className="cursor-pointer focus:bg-[#D21F2A]/15 focus:text-[#FFFFFF] flex items-center justify-between">
                <span className={l.code === "hi" ? "devanagari" : ""}>{l.name}</span>
                {l.code === lang && <Check className="w-4 h-4 text-[#D21F2A]" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {user ? (
          <button data-testid="logout-btn" onClick={logout}
            className="focusable flex items-center gap-2 px-3 py-2 rounded-lg text-[#FFFFFF] hover:bg-white/5 transition-colors">
            <LogOut className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">{t("nav_logout")}</span>
          </button>
        ) : (
          <button data-testid="header-login-btn" onClick={() => navigate("/login")}
            className="btn-gold focusable px-4 py-2 rounded-full text-sm">{t("nav_login")}</button>
        )}
      </nav>
    </header>
  );
}

function NavBtn({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="focusable flex items-center gap-2 px-3 py-2 rounded-lg text-[#FFFFFF]/90 hover:text-[#D21F2A] hover:bg-white/5 transition-colors text-sm">
      <Icon className="w-4 h-4" /> <span className="hidden lg:inline">{label}</span>
    </Link>
  );
}
